import { BUZZ_H, BUZZ_W } from './config.js';

let AC = null;

export function ac() {
  if (!AC) { try { AC = new (window.AudioContext || window.webkitAudioContext)(); } catch (e) { return null; } }
  if (AC.state === 'suspended') AC.resume();
  return AC;
}

/**
 * Eén toontje. `naar` is waar het heen gaat; laat je dat weg, dan gaat het
 * rechtstreeks naar de speakers. De muziek van Krabbelworm hangt er een eigen
 * knop tussen, zodat die in één keer stil te zetten is: toontjes die al in de
 * planning staan blijven anders doorspelen nadat je dood bent.
 */
export function tone(freq, at, dur, type, vol, naar) {
  const c = ac();
  if (!c) return;
  const o = c.createOscillator(), g = c.createGain();
  o.type = type || 'sine';
  o.frequency.value = freq;
  const t0 = c.currentTime + at;
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(vol, t0 + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g); g.connect(naar || c.destination);
  o.start(t0); o.stop(t0 + dur + 0.05);
}

export function playFanfare() {
  const mel = [[523, 0, .14], [523, .13, .1], [659, .24, .16], [784, .4, .2], [659, .58, .12], [784, .68, .14], [1046, .82, .8]];
  mel.forEach(([f, t, d]) => {
    tone(f, t, d, 'triangle', 0.17);
    tone(f * 1.5, t, d * 0.8, 'triangle', 0.06);
    tone(f * 2, t, d * 0.7, 'sawtooth', 0.035);
  });
  [523, 659, 784, 1046].forEach((f, i) => tone(f, .82 + i * 0.02, 1.1, 'triangle', 0.075));
  [1568, 2093, 2637].forEach((f, i) => tone(f, 1.0 + i * 0.09, 0.3, 'sine', 0.05));
  tone(131, 0, .5, 'sine', 0.12);
  tone(196, .82, 1.1, 'sine', 0.1);
}

const soundLast = {};

export function soundGate(key, ms) {
  const now = Date.now();
  if (soundLast[key] && now - soundLast[key] < ms) return false;
  soundLast[key] = now;
  return true;
}

export function playPop() {
  if (!soundGate('pop', 220)) return;
  tone(660, 0, 0.09, 'triangle', 0.13);
  tone(990, 0.06, 0.12, 'triangle', 0.11);
  tone(1320, 0.13, 0.18, 'sine', 0.07);
}

export function playBlip() {
  if (!soundGate('blip', 900)) return;
  tone(880, 0, 0.1, 'sine', 0.1);
  tone(1245, 0.08, 0.14, 'sine', 0.08);
}

// Kort vrolijk deuntje: er komt iemand online.
export function playHello() {
  if (!soundGate('hello', 1500)) return;
  [[523, 0, .12], [784, .1, .26]].forEach(([f, t, d]) => {
    tone(f, t, d, 'triangle', 0.12);
    tone(f * 2, t, d * 0.65, 'sine', 0.035);
  });
}

// MSN-achtige buzzer: laag zoemend duo-toontje, twee keer.
export function playBuzz() {
  [0, 0.26].forEach((t) => {
    tone(196, t, 0.2, 'square', 0.09);
    tone(147, t, 0.22, 'sawtooth', 0.07);
    tone(392, t + 0.02, 0.14, 'square', 0.04);
  });
}

export function isBuzzText(t) { return /^\s*buzz!/i.test(t || '') || /⚡/.test(t || ''); }

export const BUZZ_CLIP = (() => {
  const R = BUZZ_H / 2, D = 7, T = 23;
  const straightLen = BUZZ_W - 2 * R;
  const nStraight = Math.max(2, Math.round(straightLen / T));
  const nCap = Math.max(2, Math.round((Math.PI * R) / T));
  const pct = (x, y) => `${(x / BUZZ_W * 100).toFixed(2)}% ${(y / BUZZ_H * 100).toFixed(2)}%`;
  const pts = [];
  for (let i = 0; i < nStraight; i++) {
    pts.push(pct(R + straightLen * (i / nStraight), 0));
    pts.push(pct(R + straightLen * ((i + 0.5) / nStraight), D));
  }
  pts.push(pct(BUZZ_W - R, 0));
  for (let i = 0; i < nCap; i++) {
    const a0 = -Math.PI / 2 + Math.PI * (i / nCap), a1 = -Math.PI / 2 + Math.PI * ((i + 0.5) / nCap);
    pts.push(pct(BUZZ_W - R + Math.cos(a0) * R, R + Math.sin(a0) * R));
    pts.push(pct(BUZZ_W - R + Math.cos(a1) * (R - D), R + Math.sin(a1) * (R - D)));
  }
  pts.push(pct(BUZZ_W - R, BUZZ_H));
  for (let i = 0; i < nStraight; i++) {
    pts.push(pct(BUZZ_W - R - straightLen * (i / nStraight), BUZZ_H));
    pts.push(pct(BUZZ_W - R - straightLen * ((i + 0.5) / nStraight), BUZZ_H - D));
  }
  pts.push(pct(R, BUZZ_H));
  for (let i = 0; i < nCap; i++) {
    const a0 = Math.PI / 2 + Math.PI * (i / nCap), a1 = Math.PI / 2 + Math.PI * ((i + 0.5) / nCap);
    pts.push(pct(R + Math.cos(a0) * R, R + Math.sin(a0) * R));
    pts.push(pct(R + Math.cos(a1) * (R - D), R + Math.sin(a1) * (R - D)));
  }
  pts.push(pct(R, 0));
  return `polygon(${pts.join(',')})`;
})();

export function playParty() {
  [523, 659, 784, 1046, 1319].forEach((f, i) => {
    tone(f, i * 0.07, 0.5, 'triangle', 0.13);
    tone(f * 2, i * 0.07, 0.32, 'sine', 0.05);
  });
  [1568, 2093, 1760, 2637].forEach((f, i) => tone(f, 0.42 + i * 0.1, 0.34, 'sine', 0.06));
  tone(1046, 0.9, 0.9, 'triangle', 0.1);
  tone(1319, 0.92, 0.9, 'triangle', 0.07);
  tone(262, 0, 0.5, 'sine', 0.09);
}

export function playApplause() {
  const c = ac();
  if (!c) return;
  const dur = 1.5, buf = c.createBuffer(1, c.sampleRate * dur, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) {
    const t = i / d.length;
    const env = Math.min(1, t * 12) * Math.pow(1 - t, 1.4);
    d[i] = (Math.random() * 2 - 1) * env * (Math.random() > 0.55 ? 1 : 0.35);
  }
  const src = c.createBufferSource(); src.buffer = buf;
  const f = c.createBiquadFilter(); f.type = 'bandpass'; f.frequency.value = 1800; f.Q.value = 0.7;
  const g = c.createGain(); g.gain.value = 0.35;
  src.connect(f); f.connect(g); g.connect(c.destination);
  src.start();
}

// ---- Krabbelworm ---------------------------------------------------------

/**
 * De geluidjes van het spelletje: kort, hard en met een blokgolf, zoals een
 * handheld uit de jaren negentig. Vierkante golven klinken goedkoop en dat is
 * precies de bedoeling.
 */

/** Een balletje: twee blipjes omhoog. */
export function playWormHap() {
  tone(587, 0, 0.05, 'square', 0.08);
  tone(880, 0.045, 0.08, 'square', 0.07);
}

/** De bonus: een klein trapje omhoog. */
export function playWormBonus() {
  [523, 659, 784, 1046].forEach((f, i) => tone(f, i * 0.05, 0.1, 'square', 0.07));
}

/** Het blauwe rondje: een snelle veeg omhoog, want je gaat harder. */
export function playWormSnel() {
  [392, 523, 659, 784, 988, 1175].forEach((f, i) => tone(f, i * 0.035, 0.09, 'square', 0.06));
  tone(1568, 0.22, 0.2, 'triangle', 0.05);
}

/** Dood: vier tonen naar beneden en een lage klap eronder. */
export function playWormDood() {
  [523, 415, 330, 247].forEach((f, i) => {
    tone(f, i * 0.09, 0.16, 'square', 0.09);
    tone(f / 2, i * 0.09, 0.18, 'triangle', 0.05);
  });
  tone(98, 0.36, 0.5, 'sawtooth', 0.08);
}

/** Veld vol: hetzelfde trapje als de bonus, maar dan helemaal naar boven. */
export function playWormWinst() {
  [523, 659, 784, 1046, 1319, 1568].forEach((f, i) => {
    tone(f, i * 0.08, 0.3, 'square', 0.08);
    tone(f * 2, i * 0.08, 0.2, 'triangle', 0.03);
  });
}

/**
 * Het deuntje: vier maten die rondgaan, in a klein.
 *
 * Per maat een gebroken akkoord van vier achtsten dat twee keer klinkt, met de
 * grondtoon eronder op de eerste en de vijfde achtste. De reeks is Am, F, C, G:
 * dat is het standaardrondje waar de helft van alle chipmuziek op staat, en het
 * klinkt vooruit in plaats van rond.
 */
const RIF = [
  { bas: 110.00, akkoord: [440.00, 523.25, 659.25, 523.25] },
  { bas: 87.31, akkoord: [349.23, 440.00, 523.25, 440.00] },
  { bas: 130.81, akkoord: [523.25, 659.25, 783.99, 659.25] },
  { bas: 98.00, akkoord: [392.00, 493.88, 587.33, 493.88] }
];

let muziek = null;

/**
 * Eén maat inplannen en meteen een klok zetten voor de volgende.
 *
 * Per maat wordt het tempo opnieuw gevraagd, dus als je een niveau stijgt gaat
 * het deuntje mee omhoog: 155 milliseconden per achtste op niveau 1, 107 op
 * niveau 9. Niet per toon, want dan verschuift het ritme halverwege een maat.
 */
function maat() {
  if (!muziek) return;
  const niveau = Math.max(1, Math.min(9, muziek.niveauGeef() || 1));
  const achtste = 0.155 - (niveau - 1) * 0.006;
  const m = RIF[muziek.n % RIF.length];
  muziek.n += 1;
  for (let i = 0; i < 8; i++) {
    const f = m.akkoord[i % 4];
    tone(f, i * achtste, achtste * 0.9, 'square', 0.045, muziek.g);
    // Een octaaf eronder, zachtjes: dat maakt de melodie steviger zonder dat er
    // een tweede stem bij lijkt te komen.
    if (i % 2 === 0) tone(f / 2, i * achtste, achtste * 1.6, 'triangle', 0.022, muziek.g);
  }
  [0, 4].forEach((i) => tone(m.bas, i * achtste, achtste * 3, 'square', 0.05, muziek.g));
  muziek.timer = setTimeout(maat, Math.round(achtste * 8 * 1000));
}

/** Begin het deuntje. `niveauGeef` levert bij elke maat het huidige niveau. */
export function startWormMuziek(niveauGeef) {
  stopWormMuziek();
  const c = ac();
  if (!c) return;
  const g = c.createGain();
  g.gain.value = 1;
  g.connect(c.destination);
  muziek = { g, n: 0, timer: null, niveauGeef };
  maat();
}

/**
 * Zet het deuntje stil. De knop ertussen gaat in vijftig milliseconden naar nul,
 * zodat ook de toontjes die al ingepland stonden zwijgen; zonder die knop hoor je
 * na je dood nog een halve maat doorspelen.
 */
export function stopWormMuziek() {
  if (!muziek) return;
  const c = ac();
  clearTimeout(muziek.timer);
  try {
    muziek.g.gain.cancelScheduledValues(c.currentTime);
    muziek.g.gain.setValueAtTime(muziek.g.gain.value, c.currentTime);
    muziek.g.gain.linearRampToValueAtTime(0, c.currentTime + 0.05);
    const oud = muziek.g;
    setTimeout(() => { try { oud.disconnect(); } catch (e) {} }, 400);
  } catch (e) {}
  muziek = null;
}

// ---- Memory ------------------------------------------------------

/** Een kaartje omdraaien: één korte tik. */
export function playMemDraai() {
  tone(880, 0, 0.04, 'square', 0.05);
}

/** Een paar gevonden: twee tonen omhoog, kort en blij. */
export function playMemPaar() {
  tone(659, 0, 0.07, 'square', 0.07);
  tone(988, 0.06, 0.14, 'square', 0.06);
  tone(1319, 0.14, 0.18, 'triangle', 0.04);
}

/** Alles gevonden: hetzelfde trapje als bij een vol veld in Krabbelworm. */
export function playMemKlaar() {
  [523, 659, 784, 1046, 1319].forEach((f, i) => {
    tone(f, i * 0.07, 0.28, 'square', 0.075);
    tone(f * 2, i * 0.07, 0.18, 'triangle', 0.03);
  });
  tone(262, 0, 0.4, 'sine', 0.07);
}

// ---- Krabbelgotchi ------------------------------------------------

/**
 * Het deuntje dat je voor je beestje kunt draaien.
 *
 * Acht noten in twee zinnetjes: eerst omhoog (C-E-G-C), dan een klein huppeltje
 * terug (B-G-A-G). Vier tellen in totaal, want langer dan dat wil je niet horen
 * elke keer dat je op het knopje drukt.
 *
 * Twee stemmen: de melodie als blokgolf, en er een octaaf onder een zachte
 * driehoek. Dat is dezelfde opzet als de muziek van de Krabbellijn, zodat de app
 * één geluid houdt.
 */
export function playBeestDeun() {
  const noten = [523, 659, 784, 1047, 988, 784, 880, 784];
  const duur = 0.16;
  noten.forEach((f, i) => {
    const t = i * duur;
    tone(f, t, duur * 0.9, 'square', 0.055);
    tone(f / 2, t, duur * 0.9, 'triangle', 0.028);
  });
  // Een laatste lange noot, zodat het deuntje af klinkt en niet afgekapt.
  tone(1047, noten.length * duur, 0.34, 'square', 0.05);
  tone(523, noten.length * duur, 0.34, 'triangle', 0.03);
}

// ---- Krabbellijn ------------------------------------------------

/** Een stap doorgeven: het tuutje van een hoorn die wordt neergelegd. */
export function playKetStuur() {
  tone(988, 0, 0.09, 'square', 0.06);
  tone(1319, 0.08, 0.16, 'triangle', 0.05);
}

/** De keten is vol: twee keer een belletje, als een telefoon die overgaat. */
export function playKetKlaar() {
  [0, 0.45].forEach((t) => {
    for (let n = 0; n < 6; n++) {
      tone(n % 2 ? 1047 : 1319, t + n * 0.045, 0.05, 'square', 0.055);
    }
  });
}

/** Een tik van de klok als de tijd bijna op is. */
export function playKetTik() {
  tone(1568, 0, 0.03, 'square', 0.04);
}

/**
 * ---- het deuntje van de Krabbellijn -----------------------------------
 *
 * Vier maten in C, en de melodie vertelt het spel: hij klimt drie keer op
 * (G-E-C, A-F-D, B-G-E) en tuimelt in de vierde maat in één keer helemaal naar
 * beneden. Onderweg zit één noot die er niet hoort, de bes in die tuimel: dat is
 * de knik waar het verhaal ontspoort.
 *
 * Eronder loopt een wandelbas van twee noten per maat, en op de tel na één en na
 * drie tikt een kort hoog toontje mee. Dat huppelt.
 *
 * Aparte knop tussen de tonen en de speakers, net als bij Krabbelworm: zo is het
 * in één keer stil te zetten zonder dat wat al gepland staat nog doorspeelt.
 */
const LIJN_MATEN = [
  { mel: [392.00, 0, 329.63, 0, 261.63, 329.63, 392.00, 0], bas: [65.41, 98.00, 65.41, 98.00] },
  { mel: [440.00, 0, 349.23, 0, 293.66, 349.23, 440.00, 0], bas: [87.31, 130.81, 87.31, 130.81] },
  { mel: [493.88, 0, 392.00, 0, 329.63, 392.00, 493.88, 0], bas: [98.00, 146.83, 98.00, 146.83] },
  // De tuimel omlaag, met de bes op de tweede tel: de knik in het verhaal.
  { mel: [523.25, 466.16, 440.00, 392.00, 349.23, 329.63, 293.66, 261.63], bas: [65.41, 65.41, 98.00, 98.00] }
];

/** Zoveel duurt een achtste. Vier maten van acht is dan een lus van ruim vijf tellen. */
const LIJN_ACHTSTE = 0.16;

let lijnMuziek = null;

function lijnMaat() {
  if (!lijnMuziek) return;
  const m = LIJN_MATEN[lijnMuziek.n % LIJN_MATEN.length];
  lijnMuziek.n += 1;
  m.mel.forEach((f, i) => {
    if (!f) return;
    tone(f, i * LIJN_ACHTSTE, LIJN_ACHTSTE * 0.85, 'square', 0.038, lijnMuziek.g);
  });
  m.bas.forEach((f, i) => {
    tone(f, i * 2 * LIJN_ACHTSTE, LIJN_ACHTSTE * 1.7, 'triangle', 0.042, lijnMuziek.g);
  });
  // Het huppeltje: na de eerste en na de derde tel.
  [1, 5].forEach((i) => tone(1568, i * LIJN_ACHTSTE, 0.03, 'square', 0.018, lijnMuziek.g));
  lijnMuziek.timer = setTimeout(lijnMaat, Math.round(LIJN_ACHTSTE * 8 * 1000));
}

export function startLijnMuziek() {
  if (lijnMuziek) return;
  const c = ac();
  if (!c) return;
  const g = c.createGain();
  g.gain.value = 1;
  g.connect(c.destination);
  lijnMuziek = { g, n: 0, timer: null };
  lijnMaat();
}

export function stopLijnMuziek() {
  if (!lijnMuziek) return;
  if (lijnMuziek.timer) clearTimeout(lijnMuziek.timer);
  const oud = lijnMuziek.g;
  try {
    const c = ac();
    // Uitfaden in plaats van afkappen: een knop die je dichtdraait klikt niet.
    oud.gain.setValueAtTime(oud.gain.value, c.currentTime);
    oud.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.12);
  } catch (e) {}
  setTimeout(() => { try { oud.disconnect(); } catch (e) {} }, 400);
  lijnMuziek = null;
}
